import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import blogPosts from "../data/blogData";
import {
  TrendingUp,
  ChevronDown,
  Building2,
  MapPin,
  Briefcase,
  Lightbulb,
  CheckCircle,
  Trophy,
  BarChart3,
  Target,
  HelpCircle,
  Flag,
} from "lucide-react";

export default function BlogDetails() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    setIsVisible(true);
    // Find the post by ID from static data
    const foundPost = blogPosts.find(p => p.id === parseInt(id));
    if (foundPost) {
      setPost(foundPost);
    }
  }, [id]);

  // Initialize first section as expanded
  useEffect(() => {
    if (post) {
      // Extract first h2 section ID
      const firstSectionId = post.content.split('<h2')[1]?.match(/id="([^"]+)"/)?.[1];
      
      if (firstSectionId && Object.keys(expandedSections).length === 0) {
        setExpandedSections({ [firstSectionId]: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  // Map section IDs to icons
  const getIconForSection = (sectionId) => {
    const iconMap = {
      "market-reality": Building2,
      "active-markets": MapPin,
      "advisory-philosophy": Briefcase,
      "services": CheckCircle,
      "client-questions": Lightbulb,
      "location-intelligence": MapPin,
      "residential-decisions": Building2,
      "commercial-assets": BarChart3,
      "investment-thinking": TrendingUp,
      "transparency": Trophy,
      "conclusion": Flag,
      "faq": HelpCircle,
    };
    return iconMap[sectionId] || Target;
  };

  // Highlight "Landmarks Realty" in HTML content
  const highlightLandmarksRealty = (html) => {
    return html.replace(/Landmarks Realty/g, '<span class="landmarks-highlight">Landmarks Realty</span>');
  };

  // Generate table of contents from content
  const generateTableOfContents = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const headings = Array.from(doc.querySelectorAll("h2, h3"));
    
    return headings.map((heading) => ({
      level: heading.tagName,
      id: heading.id || heading.textContent.toLowerCase().replace(/\s+/g, "-"),
      text: heading.textContent,
    }));
  };

  // Parse content and create expandable h2 sections
  const parseAndRenderContent = (htmlContent, expandedSections, setExpandedSections) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const elements = Array.from(doc.body.children);
    
    const sections = [];
    let currentSection = null;

    elements.forEach((el, index) => {
      if (el.tagName === "H2") {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = {
          title: el.textContent,
          id: el.id || `section-${index}`,
          content: [],
          isH2: true,
        };
      } else if (currentSection) {
        currentSection.content.push(highlightLandmarksRealty(el.outerHTML));
      } else {
        sections.push({
          content: [highlightLandmarksRealty(el.outerHTML)],
          isH2: false,
        });
      }
    });

    if (currentSection) {
      sections.push(currentSection);
    }

    return sections.map((section, idx) => {
      if (section.isH2) {
        const isExpanded = expandedSections[section.id];
        const IconComponent = getIconForSection(section.id);
        return (
          <div
            key={idx}
            id={section.id}
            className="bg-white dark:bg-slate-900 rounded-lg shadow-md border border-gray-100 overflow-hidden"
          >
            <button
              onClick={() =>
                setExpandedSections((prev) => ({
                  ...prev,
                  [section.id]: !prev[section.id],
                }))
              }
              className="w-full flex items-center justify-between gap-4 p-6 hover:bg-indigo-50/80 transition-all duration-300 text-left group rounded-t-lg"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center flex-shrink-0 group-hover:from-indigo-600 group-hover:to-purple-600 transition-all duration-300">
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-700 transition-colors duration-300">
                  {section.title}
                </h2>
              </div>
              <div
                className={`flex-shrink-0 transition-transform duration-300 ${
                  isExpanded ? "rotate-180" : ""
                }`}
              >
                <ChevronDown className="w-6 h-6 text-gray-400 dark:text-slate-500" />
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-gray-100 dark:border-slate-700 px-6 py-4 bg-gray-50/30 dark:bg-slate-800/50">
                <div className="prose prose-lg max-w-none dark:prose-invert
                     prose-headings:text-gray-900 dark:prose-headings:text-slate-100 prose-headings:font-bold
                     prose-h3:text-2xl prose-h3:mt-6 prose-h3:mb-4 prose-h3:text-blue-900 dark:prose-h3:text-sky-300
                     prose-h4:text-xl prose-h4:mt-4 prose-h4:mb-3 prose-h4:text-gray-800 dark:prose-h4:text-slate-200
                     prose-h5:text-lg prose-h5:mt-3 prose-h5:mb-2 prose-h5:text-gray-700 dark:prose-h5:text-slate-300 prose-h5:font-semibold
                     prose-h6:text-base prose-h6:mt-2 prose-h6:mb-2 prose-h6:text-gray-600 dark:prose-h6:text-slate-400 prose-h6:font-semibold
                     prose-p:text-gray-700 dark:prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-justify
                     prose-ul:text-gray-700 dark:prose-ul:text-slate-300 prose-ul:space-y-2
                     prose-li:text-gray-700 dark:prose-li:text-slate-300 prose-li:text-justify
                     prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
                     prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:bg-blue-50 dark:prose-blockquote:bg-slate-800/80 prose-blockquote:py-2 prose-blockquote:text-justify">
                  {section.content.map((html, contentIdx) => (
                    <div
                      key={contentIdx}
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div
            key={idx}
            className="prose prose-lg max-w-none dark:prose-invert
                     prose-headings:text-gray-900 dark:prose-headings:text-slate-100 prose-headings:font-bold
                     prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-slate-600 prose-h2:pb-2
                     prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4 prose-h3:text-blue-900 dark:prose-h3:text-sky-300
                     prose-h4:text-xl prose-h4:mt-8 prose-h4:mb-3 prose-h4:text-gray-800 dark:prose-h4:text-slate-200
                     prose-h5:text-lg prose-h5:mt-6 prose-h5:mb-3 prose-h5:text-gray-700 dark:prose-h5:text-slate-300 prose-h5:font-semibold
                     prose-h6:text-base prose-h6:mt-4 prose-h6:mb-2 prose-h6:text-gray-600 dark:prose-h6:text-slate-400 prose-h6:font-semibold prose-h6:tracking-wide
                     prose-p:text-gray-700 dark:prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-justify
                     prose-ul:text-gray-700 dark:prose-ul:text-slate-300 prose-ul:space-y-2
                     prose-li:text-gray-700 dark:prose-li:text-slate-300 prose-li:text-justify
                     prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
                     prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:bg-blue-50 dark:prose-blockquote:bg-slate-800/80 prose-blockquote:py-2 prose-blockquote:text-justify"
          >
            {section.content.map((html, contentIdx) => (
              <div
                key={contentIdx}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            ))}
          </div>
        );
      }
    });
  };

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950">
        <div className="text-center px-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Blog Post Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400">The blog post you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* NAVBAR */}
      <Navbar />

      {/* ANIMATED HEADER */}
      <header className="pt-20 sm:pt-24 lg:pt-0 relative min-h-screen flex items-center overflow-hidden">
        {/* Animated background with particles */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-purple-900 to-pink-900">
          {/* Animated gradient blobs */}
          <div className="absolute inset-0 opacity-40">
            <div className="absolute top-0 -left-10 sm:-left-20 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
            <div className="absolute top-0 -right-10 sm:-right-20 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-10 sm:-bottom-20 left-1/2 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
            <div className="absolute top-1/2 left-1/4 w-[200px] sm:w-[300px] lg:w-[400px] h-[200px] sm:h-[300px] lg:h-[400px] bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-6000"></div>
          </div>

          {/* Floating particles */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/30 rounded-full animate-float"></div>
            <div className="absolute top-1/3 right-1/4 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-white/20 rounded-full animate-float animation-delay-1000"></div>
            <div className="absolute bottom-1/3 left-1/3 w-2 sm:w-2.5 h-2 sm:h-2.5 bg-white/25 rounded-full animate-float animation-delay-2000"></div>
            <div className="absolute top-2/3 right-1/3 w-0.5 sm:w-1 h-0.5 sm:h-1 bg-white/30 rounded-full animate-float animation-delay-3000"></div>
            <div className="absolute bottom-1/4 left-2/3 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/20 rounded-full animate-float animation-delay-4000"></div>
            <div className="absolute top-1/2 left-1/2 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-white/25 rounded-full animate-float animation-delay-5000"></div>
          </div>

          {/* Grid overlay */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>

          {/* Radial gradient overlay */}
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/40"></div>
        </div>

        {/* Content */}
        <div className={`relative z-10 w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 text-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="max-w-5xl mx-auto">
            {/* Animated badge */}
            <div className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6 sm:mb-8 group hover:bg-white/15 transition-all duration-300 cursor-default ${isVisible ? "animate-fade-in-scale" : "opacity-0 scale-95"}`}>
              <div className="relative">
                <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-300 animate-pulse-glow" />
                <div className="absolute inset-0 bg-yellow-300 blur-md opacity-50 animate-pulse"></div>
              </div>
              <span className="text-white/95 text-xs sm:text-sm font-medium tracking-wide">
                Real Estate Insights & Market Intelligence
              </span>
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>

            {/* Main heading with staggered animation */}
            <div className="mb-6 sm:mb-8">
              <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white tracking-tight leading-[1.1] transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <span className="inline-block hover:scale-105 transition-transform duration-300">
                  {post.title}
                </span>
              </h1>
            </div>

            {/* Article meta info */}
            <div className={`flex flex-wrap items-center justify-center gap-4 sm:gap-6 lg:gap-8 mt-6 sm:mt-8 lg:mt-10 transition-all duration-1000 delay-600 px-4 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group cursor-default">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm font-medium">By {post.author}</span>
              </div>
              <div className="w-px h-3 sm:h-4 bg-white/20"></div>
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group cursor-default">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full animate-pulse animation-delay-1000"></div>
                <span className="text-xs sm:text-sm font-medium">{new Date(post.date).toLocaleDateString()}</span>
              </div>
              <div className="w-px h-3 sm:h-4 bg-white/20"></div>
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group cursor-default">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full animate-pulse animation-delay-2000"></div>
                <span className="text-xs sm:text-sm font-medium">{post.readTime}</span>
              </div>
            </div>

            {/* Article excerpt */}
            <p className={`text-sm sm:text-base md:text-lg lg:text-xl text-white/90 max-w-4xl mx-auto mb-8 sm:mb-10 leading-relaxed font-light px-4 transition-all duration-1000 delay-800 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              {post.excerpt}
            </p>
          </div>
        </div>

        {/* Enhanced scroll indicator */}
        <button
          type="button"
          aria-label="Scroll to article"
          onClick={() =>
            document.getElementById('main-content')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
          className="absolute bottom-6 sm:bottom-8 lg:bottom-10 left-1/2 z-30 -translate-x-1/2 flex flex-col items-center gap-2 sm:gap-3 animate-bounce-slow cursor-pointer group rounded-full border-0 bg-transparent px-5 py-3 text-left hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <span className="text-white/60 text-[10px] sm:text-xs font-medium uppercase tracking-wider group-hover:text-white transition-colors">
            Scroll to read
          </span>
          <div className="w-6 h-9 sm:w-7 sm:h-11 border-2 border-white/40 rounded-full flex items-start justify-center p-1.5 sm:p-2 group-hover:border-white/80 transition-all">
            <div className="w-1 h-2 sm:w-1.5 sm:h-3 bg-white/70 rounded-full animate-scroll-down"></div>
          </div>
        </button>

        {/* Floating shapes for depth */}
        <div className="absolute top-20 right-20 w-16 h-16 lg:w-20 lg:h-20 border-2 border-white/10 rounded-2xl rotate-12 animate-float-slow hidden lg:block"></div>
        <div className="absolute bottom-32 left-20 w-12 h-12 lg:w-16 lg:h-16 border-2 border-white/10 rounded-full animate-float-slow animation-delay-2000 hidden lg:block"></div>
        <div className="absolute top-1/2 right-32 w-10 h-10 lg:w-12 lg:h-12 border-2 border-white/10 rounded-lg -rotate-12 animate-float-slow animation-delay-4000 hidden lg:block"></div>
      </header>

      <style>{`
  @keyframes blob {
    0%, 100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(40px, -60px) scale(1.15);
    }
    66% {
      transform: translate(-30px, 30px) scale(0.95);
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px) translateX(0px);
      opacity: 0.3;
    }
    50% {
      transform: translateY(-30px) translateX(20px);
      opacity: 0.8;
    }
  }

  @keyframes float-slow {
    0%, 100% {
      transform: translateY(0px) rotate(0deg);
    }
    50% {
      transform: translateY(-20px) rotate(5deg);
    }
  }

  @keyframes fade-in-scale {
    0% {
      opacity: 0;
      transform: scale(0.9);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes pulse-glow {
    0%, 100% {
      opacity: 1;
      filter: brightness(1);
    }
    50% {
      opacity: 0.8;
      filter: brightness(1.3);
    }
  }

  @keyframes gradient-shift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes scroll-down {
    0% {
      transform: translateY(0);
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      transform: translateY(12px);
      opacity: 0;
    }
  }

  @keyframes bounce-slow {
    0%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    50% {
      transform: translateX(-50%) translateY(-10px);
    }
  }

  .animate-blob {
    animation: blob 9s infinite;
  }

  .animate-float {
    animation: float 8s ease-in-out infinite;
  }

  .animate-float-slow {
    animation: float-slow 10s ease-in-out infinite;
  }

  .animate-fade-in-scale {
    animation: fade-in-scale 0.6s ease-out;
  }

  .animate-pulse-glow {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .animate-gradient-shift {
    animation: gradient-shift 6s ease infinite;
  }

  .animate-scroll-down {
    animation: scroll-down 2s ease-in-out infinite;
  }

  .animate-bounce-slow {
    animation: bounce-slow 3s ease-in-out infinite;
  }

  .animation-delay-1000 {
    animation-delay: 1s;
  }

  .animation-delay-2000 {
    animation-delay: 2s;
  }

  .animation-delay-3000 {
    animation-delay: 3s;
  }

  .animation-delay-4000 {
    animation-delay: 4s;
  }

  .animation-delay-5000 {
    animation-delay: 5s;
  }

  .animation-delay-6000 {
    animation-delay: 6s;
  }

  .bg-gradient-radial {
    background: radial-gradient(circle at center, var(--tw-gradient-stops));
  }

  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .landmarks-highlight {
    font-style: italic;
    font-weight: 600;
  }
`}</style>

      {/* TABLE OF CONTENTS */}
      <section className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Table of Contents</h2>
          <nav className="space-y-2">
            {generateTableOfContents(post.content).map((item, idx) => {
              const handleTOCClick = (e) => {
                e.preventDefault();
                
                if (item.level === "H2") {
                  // Expand the h2 section
                  setExpandedSections((prev) => ({
                    ...prev,
                    [item.id]: true,
                  }));
                  
                  // Scroll to the section
                  setTimeout(() => {
                    const element = document.getElementById(item.id);
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }, 100);
                } else if (item.level === "H3") {
                  // Find the parent h2 section id by looking for h2 in the TOC before this h3
                  let parentH2Id = null;
                  for (let i = idx - 1; i >= 0; i--) {
                    const prevItem = generateTableOfContents(post.content)[i];
                    if (prevItem.level === "H2") {
                      parentH2Id = prevItem.id;
                      break;
                    }
                  }
                  
                  // Expand the parent h2 if found
                  if (parentH2Id) {
                    setExpandedSections((prev) => ({
                      ...prev,
                      [parentH2Id]: true,
                    }));
                  }
                  
                  // Scroll to the h3 element
                  setTimeout(() => {
                    const element = document.getElementById(item.id);
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }, 100);
                }
              };

              return (
                <a
                  key={idx}
                  href={`#${item.id}`}
                  onClick={handleTOCClick}
                  className={`block text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm transition-colors ${
                    item.level === "H2" ? "font-medium" : "ml-4"
                  }`}
                >
                  {item.level === "H3" && "• "}
                  {item.text}
                </a>
              );
            })}
          </nav>
        </div>
      </section>

      {/* CONTENT */}
      <main id="main-content" className="scroll-mt-24 max-w-4xl mx-auto px-4 py-16 sm:scroll-mt-28">
        <article className="max-w-none space-y-4">
          {parseAndRenderContent(post.content, expandedSections, setExpandedSections)}
        </article>
      </main>

      <Footer />
    </div>
  );
}
